import { Box, Button, Fade, IconButton, Snackbar, Toolbar, Typography } from '@material-ui/core'
import { SchoolType, Title1Program } from '../types'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleLeft, faAngleRight, faLocationDot, faSchool, faTimes } from '@fortawesome/pro-solid-svg-icons'
import { Stack } from '@mui/material'
import PreviewDrawer from './PreviewDrawer'
import classNames from 'classnames'
import { Link } from 'react-router-dom'
import ContactHighSchoolCounselorCallToAction from 'components/ContactHighSchoolCounselorCallToAction'
import { format } from 'date-fns'
import { useState } from 'react'
import useGetHighschoolPreviewData, { HighschoolData } from './useGetHighschoolPreviewData'
import SchoolDetailsContainer from './SchoolDetailsContainer'
import ScoirBadge from 'components/ScoirBadge/ScoirBadge'
import SchoolTypePill from '../components/SchoolTypePill'

type ExplorePreviewRecord = {
  id: string
  name: string
  city: string
  state: string
  schoolType?: SchoolType | undefined
  seniorClassSize?: number | undefined
  nextVisitDate?: string | undefined
  title1Program?: Title1Program | undefined
  pctFreeReducedLunch?: number | undefined
  active?: boolean
}
const useStyles = makeStyles<Theme>((theme) => {
  const border = `1px solid ${theme.palette.divider}`
  return {
    previewToolbar: {
      backgroundColor: theme.palette.primary.main,
      justifyContent: 'space-between',
      width: '100%',
    },
    toolbarActionButton: {
      color: 'white',
      fontSize: '1.5em',
    },
    studentPreviewDrawerHeader: {
      borderBottom: border,
    },
  }
})

interface HighschoolPreviewDrawerNav {
  onClose: () => void
  highschool?: ExplorePreviewRecord | null
  onClickNext: (highschoolId?: string) => void
  hasNext: boolean
  onClickPrev: (highschoolId?: string) => void
  hasPrevious: boolean
  navDisabled?: boolean
}

const HighschoolPreviewDrawerNav = ({
  onClose,
  highschool,
  onClickNext,
  hasNext,
  onClickPrev,
  hasPrevious,
  navDisabled,
}: HighschoolPreviewDrawerNav) => {
  const classes = useStyles()
  return (
    <Toolbar
      variant="dense"
      className={classes.previewToolbar}
    >
      <Box>
        <IconButton
          className={classes.toolbarActionButton}
          aria-label="Previous Student"
          edge="start"
          size="medium"
          disabled={!hasPrevious || navDisabled}
          onClick={() => {
            onClickPrev(highschool?.id)
          }}
        >
          <FontAwesomeIcon icon={faAngleLeft} />
        </IconButton>
        <IconButton
          aria-label="Next Student"
          className={classes.toolbarActionButton}
          size="medium"
          disabled={!hasNext || navDisabled}
          onClick={() => onClickNext(highschool?.id)}
        >
          <FontAwesomeIcon icon={faAngleRight} />
        </IconButton>
      </Box>
      <IconButton
        className={classes.toolbarActionButton}
        edge="end"
        size="medium"
        onClick={onClose}
      >
        <FontAwesomeIcon icon={faTimes} />
      </IconButton>
    </Toolbar>
  )
}

interface HighschoolPreviewDrawer {
  open: boolean
  onClose: () => void
  highschool?: ExplorePreviewRecord | null
  onClickNext: (highschoolId?: string) => void
  hasNext: boolean
  onClickPrev: (highschoolId?: string) => void
  hasPrevious: boolean
  dataIsLoading: boolean
}

const HighschoolPreviewDrawer = ({
  open,
  dataIsLoading,
  onClose,
  highschool,
  onClickNext,
  hasNext,
  onClickPrev,
  hasPrevious,
}: HighschoolPreviewDrawer) => {
  const [innerModalOpen, setInnerModalOpen] = useState(false)
  const highschoolResponse = useGetHighschoolPreviewData({ highschoolId: highschool?.id, enabled: open })
  return (
    <PreviewDrawer
      open={open}
      onClose={onClose}
      innerModalOpen={innerModalOpen}
    >
      {open && (
        <Stack
          direction="column"
          flexGrow={1}
        >
          <HighschoolPreviewDrawerNav
            navDisabled={dataIsLoading}
            onClose={onClose}
            highschool={highschool}
            onClickNext={onClickNext}
            hasNext={hasNext}
            onClickPrev={onClickPrev}
            hasPrevious={hasPrevious}
          />

          {highschoolResponse.data?.highschool && !highschoolResponse.isLoading && (
            <Fade
              key={highschoolResponse.data.highschool.id}
              in={true}
              timeout={600}
              style={{ flexGrow: 1, flexDirection: 'column', display: 'flex' }}
            >
              <Box flexGrow={1}>
                <PreviewHeader
                  highschool={highschoolResponse.data.highschool}
                  setInnerModalOpen={setInnerModalOpen}
                />
                <PreviewContent highschool={highschoolResponse.data.highschool} />
              </Box>
            </Fade>
          )}
          <Snackbar
            open={highschoolResponse.isError}
            message="There was an error getting the high school."
          />
        </Stack>
      )}
    </PreviewDrawer>
  )
}

const PreviewContent = ({ highschool }: { highschool: HighschoolData }) => {
  return (
    <Stack direction="column">
      <SchoolDetailsContainer highschool={highschool} />
    </Stack>
  )
}

const useHeaderStyles = makeStyles((theme) => ({
  expandedCardHeader: {
    display: 'flex',
    maxWidth: '55%',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
    },
    alignItems: 'center',
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2, 2, 0, 2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  headerActionBar: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  headerActionItemContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
  },
}))
interface PreviewHeader {
  highschool: HighschoolData
  setInnerModalOpen: (v: boolean) => void
}
const PreviewHeader = ({ highschool, setInnerModalOpen }: PreviewHeader) => {
  const style = useHeaderStyles()

  const viewLink = `selectedSchools=${encodeURIComponent(
    JSON.stringify([
      {
        id: highschool.id,
        name: highschool.name,
      },
    ]),
  )}`

  const date = highschool?.nextVisitDate ? format(new Date(highschool.nextVisitDate), 'yyyyMMdd') : ''

  // Some highschools returning urls that don't have http://, this should prevent that from breaking.
  const ensureHttpPrefix = (url: string) => {
    if (!url) return ''
    return url.startsWith('http://') || url.startsWith('https://') ? url : `http://${url}`
  }

  // This is just to prevent displaying websites that are insanely long
  const truncateText = (text: string, maxLength: number) => {
    if (!text) return ''
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text
  }

  return (
    <Box>
      <Stack
        direction="column"
        sx={(theme) => ({
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
      >
        <Box className={classNames(style.titleContainer)}>
          <div className={style.expandedCardHeader}>
            <Typography
              variant="h5"
              color="textPrimary"
            >
              {highschool.name}
            </Typography>
          </div>

          <Box className={style.headerActionBar}>
            <Box className={style.headerActionItemContainer}>
              {highschool.isActive && (
                <>
                  <Button
                    component={Link}
                    disabled={!highschool?.nextVisitDate}
                    size="small"
                    variant="contained"
                    color="primary"
                    to={`${import.meta.env.BASE_URL}/recruit/visits/view/week?focus=${date}&${viewLink}`}
                  >
                    View Availability
                  </Button>
                  <ContactHighSchoolCounselorCallToAction
                    onOpenChange={setInnerModalOpen}
                    variant="outlined"
                    color="primary"
                    size="small"
                    highSchoolId={highschool.id}
                    highSchoolName={highschool.name}
                    contactUserId={highschool.contactUserId}
                  >
                    Message
                  </ContactHighSchoolCounselorCallToAction>
                </>
              )}
            </Box>
          </Box>
        </Box>
        <Stack
          direction="row"
          padding={2}
        >
          <Stack
            direction="row"
            flex={1}
            gap={2}
            alignItems="center"
          >
            <FontAwesomeIcon
              style={{ position: 'relative', bottom: 1 }}
              size="lg"
              icon={faSchool}
            />
            <Stack
              direction="row"
              alignItems={'center'}
              gap={0.5}
            >
              {highschool.schoolType ? <SchoolTypePill schoolType={highschool.schoolType} /> : 'Unknown'}
              {highschool.isActive && <ScoirBadge />}
            </Stack>
          </Stack>
          <Stack
            direction="row"
            gap={2}
            flex={1}
            alignItems="center"
          >
            <FontAwesomeIcon
              size="lg"
              icon={faLocationDot}
            />
            <Stack direction="column">
              {highschool.address === '' ? 'Unknown Address' : highschool.address}
              <br />
              {highschool.city !== '' ? `${highschool.city},` : ''}{' '}
              {highschool.state !== '' ? `${highschool.state}` : ''} {highschool.zip ? `${highschool.zip}` : ''}
              {highschool.website && (
                <Box width="100%">
                  <a
                    target="_blank"
                    rel="noopener"
                    href={ensureHttpPrefix(highschool.website)}
                    title={highschool.website}
                  >
                    {truncateText(highschool.website, 25)}
                  </a>
                </Box>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  )
}

export default HighschoolPreviewDrawer
